// SPDX-License-Identifier: AGPL-3.0-only

import { ApiRequestError, requestJson } from "./http.js";

export interface AuthenticatedUser {
  readonly id: string;
  readonly email: string;
  readonly displayName: string;
  readonly systemRoles: readonly string[];
}

export interface AuthenticatedTenant {
  readonly id: string;
  readonly slug: string;
  readonly displayName: string;
  readonly role: string;
}

export interface AuthenticatedSession {
  readonly user: AuthenticatedUser;
  readonly tenants: readonly AuthenticatedTenant[];
  readonly expiresAt: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readString(value: unknown, fieldName: string): string {
  if (typeof value !== "string") {
    throw new Error(`Authentication response field ${fieldName} is invalid`);
  }

  return value;
}

function readStringArray(value: unknown, fieldName: string): readonly string[] {
  if (!Array.isArray(value) || !value.every((item: unknown): boolean => typeof item === "string")) {
    throw new Error(`Authentication response field ${fieldName} is invalid`);
  }

  return value as string[];
}

function readSession(payload: unknown): AuthenticatedSession {
  if (!isRecord(payload) || !isRecord(payload.user) || !Array.isArray(payload.tenants)) {
    throw new Error("Authentication response is invalid");
  }

  const user: AuthenticatedUser = {
    id: readString(payload.user.id, "user.id"),
    email: readString(payload.user.email, "user.email"),
    displayName: readString(payload.user.displayName, "user.displayName"),
    systemRoles: readStringArray(payload.user.systemRoles, "user.systemRoles"),
  };
  const tenants: AuthenticatedTenant[] = payload.tenants.map(
    (tenant: unknown, index: number): AuthenticatedTenant => {
      if (!isRecord(tenant)) {
        throw new Error(`Authentication response tenant ${index} is invalid`);
      }

      return {
        id: readString(tenant.id, `tenants.${index}.id`),
        slug: readString(tenant.slug, `tenants.${index}.slug`),
        displayName: readString(tenant.displayName, `tenants.${index}.displayName`),
        role: readString(tenant.role, `tenants.${index}.role`),
      };
    },
  );

  return {
    user,
    tenants,
    expiresAt: readString(payload.expiresAt, "expiresAt"),
  };
}

export async function login(email: string, password: string): Promise<AuthenticatedSession> {
  const payload: unknown = await requestJson("/api/v1/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });

  return readSession(payload);
}

export async function loadSession(): Promise<AuthenticatedSession | null> {
  try {
    const payload: unknown = await requestJson("/api/v1/auth/session");
    return readSession(payload);
  } catch (error: unknown) {
    if (error instanceof ApiRequestError && error.status === 401) {
      return null;
    }

    throw error;
  }
}

export async function logout(): Promise<void> {
  await requestJson("/api/v1/auth/logout", { method: "POST" });
}
